import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api } from '../../../lib/api-client';
import {
  WS_EVENTS,
  type ChecklistAgentProposalPayload,
  type ChecklistUpdatedPayload,
} from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

export interface ChecklistSummary {
  id: string;
  name: string;
  status: 'in_progress' | 'completed' | 'abandoned' | 'suspended';
  totalItems: number;
  completedItems: number;
  criticalPending: boolean;
}

export interface AgentProposal {
  conversationId: string;
  checklistId: string;
  proposals: {
    itemId: string;
    itemName: string;
    proposedStatus: 'fulfilled' | 'not_applicable';
    confidence: number;
    rationale: string;
  }[];
  invocationId: string;
  detectedAt: string;
}

/**
 * useChecklists — Fatia E.
 *
 * Carrega checklists da conversa via REST + subscribe WS para `checklist.updated`
 * (recalc) e `checklist-agent.proposal-made` (banner). Mantém `latestProposal`
 * para o ChecklistAgentBanner consumir.
 */
export function useChecklists(conversationId: string | undefined): {
  status: 'idle' | 'loading' | 'ready' | 'error';
  checklists: ChecklistSummary[];
  latestProposal: AgentProposal | null;
  clearProposal: () => void;
} {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const [latestProposal, setLatestProposal] = useState<AgentProposal | null>(null);

  const queryKey = ['checklists', conversationId];
  const queryResult = useQuery({
    queryKey,
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) return Promise.resolve({ data: [] });
      return api.listChecklists(conversationId);
    },
  });

  useEffect(() => {
    if (conversationId === undefined) return;
    const unsubUpdate = subscribe<ChecklistUpdatedPayload>(
      WS_EVENTS.CHECKLIST_UPDATED,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        void queryClient.invalidateQueries({ queryKey });
        // Frente A — a lista de itens (useChecklistDetail) também precisa refrescar
        // quando a mudança vem de FORA do bloco (banner da IA / projeção / outro
        // operador). Sem isto, o item ficava "pendente" stale e o clique dava 409.
        void queryClient.invalidateQueries({
          queryKey: ['checklist-detail', envelope.data.checklistId],
        });
      },
    );
    const unsubProposal = subscribe<ChecklistAgentProposalPayload>(
      WS_EVENTS.CHECKLIST_AGENT_PROPOSAL_MADE,
      (envelope) => {
        if (envelope.data.conversationId !== conversationId) return;
        setLatestProposal({
          conversationId: envelope.data.conversationId,
          checklistId: envelope.data.checklistId,
          proposals: envelope.data.proposals,
          invocationId: envelope.data.invocationId,
          detectedAt: envelope.data.detectedAt,
        });
      },
    );
    return () => {
      unsubUpdate();
      unsubProposal();
    };
  }, [conversationId, queryClient, subscribe, queryKey]);

  const data = queryResult.data?.data ?? [];
  let status: 'idle' | 'loading' | 'ready' | 'error';
  if (queryResult.isError) status = 'error';
  else if (queryResult.isLoading) status = 'loading';
  else if (data.length > 0 || queryResult.isFetched) status = 'ready';
  else status = 'idle';

  const checklists: ChecklistSummary[] = data.map((c) => ({
    id: c.id,
    name: c.name,
    status: c.status,
    totalItems: c.totalItems,
    completedItems: c.completedItems,
    criticalPending: c.criticalPending,
  }));

  return {
    status,
    checklists,
    latestProposal,
    clearProposal: () => setLatestProposal(null),
  };
}
